'use client';

/**
 * renderers/hero-block.tsx — extracted from block-renderer.tsx (SPEC-244 IA16-LANDING-FE).
 * Cero cambios de lógica — VERBATIM extraction. La composición la hace
 * el dispatcher block-renderer.tsx (importa los renderers por tipo).
 * Spec ref: SPEC-059 (docs/SPEC-059-LANDING-PAGE-BUILDER.md).
 *
 * [PR #1494 — hydration mismatch defensive measure] Added `'use client'`
 * directive at line 1. Doesn't fix the hydration mismatch alone (Client
 * Components are still SSR'd by Next.js), but marks the component boundary
 * cleanly for future maintainability. The root-cause fix is the BE
 * read-time normalization in `backend/src/features/landing/helpers.ts:
 * normalizeLandingBlocksSameOriginLinks` which guarantees the renderer
 * never sees an absolute same-origin URL. The `typeof window !==
 * 'undefined'` guard below remains as a defensive layer in case BE
 * normalization is bypassed (e.g. a custom-domain handler that returns
 * blocks raw).
 */

import * as React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { isHttpUrl, isDarkHex, readableTextOn } from '../_helpers';
import type {
  HeroBlockData,
} from '@/lib/landing-types';

export function HeroBlock({
  data,
  theme,
  hasOwnBackground,
}: {
  data: HeroBlockData;
  // [FIX-LANDING-STYLING-WHITE-ON-WHITE] extend the theme type to include
  // `backgroundColor` so we can pick a readable text color when the hero
  // block carries its own per-block bg color (BLOCK-theme override).
  theme?: { primaryColor?: string; secondaryColor?: string; backgroundColor?: string };
  // [SPRINT 58 · PATCH 1] true when the block carries its own
  // theme.backgroundColor (painted by the P-143 wrapper div) — the hero
  // then respects the author's flat background instead of overpainting
  // it with the fallback gradient.
  hasOwnBackground?: boolean;
}) {
  const bgStyle: React.CSSProperties = {};
  if (isHttpUrl(data.backgroundImageUrl)) {
    bgStyle.backgroundImage = `url(${data.backgroundImageUrl})`;
    bgStyle.backgroundSize = 'cover';
    bgStyle.backgroundPosition = 'center';
  } else if (!isHttpUrl(data.backgroundVideoUrl) && !hasOwnBackground) {
    // [SPRINT 58 · PATCH 1 — hero fallback] A hero with no image, no video
    // and no per-block background used to render its hardcoded white text
    // directly over the page background (white H1 on white page = invisible
    // title — the "landing genérica se ve muy mal" bug). Now it gets a
    // dark slate → accent gradient so the copy is always readable.
    // isDarkHex guards against light text-colors leaking into the gradient.
    const c1 = isDarkHex(theme?.primaryColor) ? (theme?.primaryColor as string) : '#0f172a';
    // [FIX-LANDING-STYLING-WHITE-ON-WHITE] also guard c2 (gradient bright
    // end). Pre-fix a light `theme.secondaryColor` (e.g. yellow #FFFF00)
    // would leak into the gradient as the bright endpoint → white text
    // at that end was unreadable on the bright color. Now we fall back
    // to '#2563eb' (Tailwind blue-500, dark enough for white text) when
    // the user-picked secondaryColor is too light.
    const c2 = isDarkHex(theme?.secondaryColor) ? (theme?.secondaryColor as string) : '#2563eb';
    bgStyle.backgroundImage = `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`;
  }

  // [FIX-LANDING-STYLING-WHITE-ON-WHITE] Pick the hero text color based
  // on the effective background so white text never sits on a white bg:
  //   - Gradient OR image OR video background → `text-white` (the dark
  //     overlay below guarantees readability across the whole gradient,
  //     even when the user picked a light secondaryColor).
  //   - Per-block flat bg color (`hasOwnBackground=true`) → flip to
  //     `text-slate-900` when the user's chosen bg is light. Pre-fix the
  //     hero always used `text-white`, which became invisible whenever
  //     the admin/user picked white or any light color as the block bg.
  //   - No bg at all (defensive — shouldn't happen in practice) →
  //     `text-white` (legacy default).
  const hasVisualBg = Boolean(
    bgStyle.backgroundImage || isHttpUrl(data.backgroundVideoUrl),
  );
  const heroTextClass = hasVisualBg
    ? 'text-white'
    : readableTextOn(theme?.backgroundColor);

  return (
    <section
      className="relative min-h-[60vh] flex items-center justify-center text-center py-20 px-4"
      style={bgStyle}
    >
      {isHttpUrl(data.backgroundVideoUrl) && (
        <video
          className="absolute inset-0 w-full h-full object-cover -z-10"
          src={data.backgroundVideoUrl}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />
      )}
      {bgStyle.backgroundImage && (
        <div className="absolute inset-0 bg-black/40 -z-10" aria-hidden="true" />
      )}
      <div className={cn('max-w-3xl space-y-4', heroTextClass)}>
        {data.title ? (
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight drop-shadow-md">
            {data.title}
          </h1>
        ) : null}
        {data.subtitle ? (
          <p className="text-lg md:text-xl opacity-90 drop-shadow">{data.subtitle}</p>
        ) : null}
        {data.ctaText ? (
          <div className="pt-4">
            {(() => {
              // PR #1435 — support 3 link kinds for the hero CTA (mirrors
              // cta-block.tsx): external http(s), anchor #, internal SPA
              // route (relative path with leading slash). Pre-fix the hero
              // only handled http(s) → everything else fell into a
              // non-clickable <span>. The buggy `funnel-agenda-llamada`
              // template shipped 'qa_admin_staging/reservar' (no leading
              // slash) → hero CTA "Agendar mi llamada de 20 min" rendered
              // as a styled-but-NOT-clickable <span>. Defense-in-depth:
              // if the link is a relative path WITHOUT a leading slash but
              // contains a `/`, prepend `/` (mirrors the backend read-time
              // repair in public-landing.ts).
              const rawLink = typeof data.ctaLink === 'string' ? data.ctaLink : '';
              const isHttp = isHttpUrl(rawLink);
              const isAnchor = rawLink.startsWith('#');
              let link = rawLink;
              if (
                !isHttp &&
                !isAnchor &&
                link.length > 0 &&
                !link.startsWith('/') &&
                link.includes('/')
              ) {
                link = '/' + link;
              }
              // Anti-P-382 fix (2026-10-28): detect same-origin HTTP links
              // (e.g., https://staging.profileia.com/r/code). Pre-fix: ALL
              // http links used target=_blank → Safari/macOS blocks the
              // new tab in certain SSR contexts → buttons "don't work on
              // Mac". Post-fix: same-origin links are treated as internal
              // (no _blank, same-tab navigation). Cross-origin links still
              // use _blank (correct — external site).
              const isSameOriginHttp = isHttp && typeof window !== 'undefined' && (
                link.startsWith(window.location.origin + '/') ||
                link.startsWith(window.location.origin)
              );
              const isInternal = (link.startsWith('/') && !link.startsWith('//')) || isSameOriginHttp;
              if (isSameOriginHttp && typeof window !== 'undefined') {
                link = link.slice(window.location.origin.length) || '/';
              }
              const buttonBg = theme?.secondaryColor || '#3b82f6';
              const buttonTextClass = readableTextOn(buttonBg);
              const className = cn(
                'inline-flex items-center justify-center rounded-md px-6 py-3 text-sm font-medium shadow transition-colors',
                buttonTextClass,
              );
              const style = { backgroundColor: buttonBg };
              if (!link || (!isHttp && !isAnchor && !isInternal)) {
                return (
                  <span className={className} style={style}>
                    {data.ctaText}
                  </span>
                );
              }
              if (isInternal) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return (
                  <Link href={link as any} className={className} style={style}>
                    {data.ctaText}
                  </Link>
                );
              }
              return (
                <a
                  href={link}
                  {...(isHttp ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className={className}
                  style={style}
                >
                  {data.ctaText}
                </a>
              );
            })()}
          </div>
        ) : null}
      </div>
    </section>
  );
}
